import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { HealthPieCard } from './HealthPieCard.js';
import { ReviewerResourceCard } from './ReviewerResourceCard.js';
import { formatPt } from '../../../lib/format.js';
import type { HealthGrade } from '../../../types/project.js';
import type { CrossDivPmoUiConfig } from '../../../lib/cross-div-pmo-ui.js';

interface ProjectsSummaryCardsProps {
  /** 進行中 PJ 数（横断 PMO 時は scope に絞った件数、PI Div 時は data.summary 由来）。 */
  activeProjectCount: number;
  /** 保留中 PJ 数（同上）。 */
  onHoldCount: number;
  /** 横断 PMO UI 設定。設定時のみ Div 色の「今月中実績」カラムを追加。 */
  crossDivPmoUi: CrossDivPmoUiConfig | null;
  /** 今月中実績合計（pt）。crossDivPmoUi 設定時のみ表示される。 */
  monthlyActualPoint: number;
  /** 健康度パイチャート用集計データ。 */
  healthStats: Parameters<typeof HealthPieCard>[0]['healthStats'];
  /** 健康度パイの中央に表示する平均グレード。 */
  healthGrade: HealthGrade;
  /** 保留中 PJ 数をクリックしたときの遷移コールバック。 */
  onShowOnHold: () => void;
}

/**
 * /projects 上部のサマリーカード 3 枚（PJ数 / レビュアーリソース / 健康度Pie）。
 *
 * 左カードは「進行中PJ数」と「保留中PJ数」を 1 枚に統合する（保留中の数字をクリックすると
 * 従来どおり保留中案件一覧へ遷移する）。横断 PMO の場合はさらに Div 色で「今月中実績」を
 * 同じカード内に並べる（3 カラムになるため文字・数字を一段小さくして収める）。
 * 中央のレビュアーリソースは一覧を持つため、左右のカードから幅を少し借りて広く取る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business プロジェクト管理ページ上部のサマリーカード描画。進行中PJ数と保留中PJ数を左カードへ統合し、中央にレビュアーリソース（左右より広い）、右に健康度パイを並べる
 * @graph-connects HealthPieCard [calls] 健康度パイチャート描画
 * @graph-connects ReviewerResourceCard [calls] レビュアーリソースカード描画
 */
export function ProjectsSummaryCards({
  activeProjectCount,
  onHoldCount,
  crossDivPmoUi,
  monthlyActualPoint,
  healthStats,
  healthGrade,
  onShowOnHold,
}: ProjectsSummaryCardsProps) {
  const { t } = useTranslation();
  // 横断 PMO は 1 枚に 3 カラム入るため、ラベル・数字を一段小さくして収める。
  const labelClass = crossDivPmoUi ? 'text-xs' : 'text-sm';
  // 数字は等幅 (JetBrains Mono) + 桁揃えにして、列をまたいでも字面が揃うようにする。
  const countClass = `font-mono tabular-nums font-bold ${crossDivPmoUi ? 'text-4xl' : 'text-5xl'}`;
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-[0.85fr_1.3fr_0.85fr]">
      <Card className="animate-content-reveal h-full flex flex-col">
        <CardContent className="flex-1 flex items-center">
          <div
            className={`w-full text-center grid gap-2 ${
              crossDivPmoUi ? 'grid-cols-3' : 'grid-cols-2'
            }`}
          >
            {/* ラベル + 数字を 1 ブロックとしてカード内で縦中央に揃える。 */}
            <div className="flex flex-col items-center justify-center">
              <p className={`${labelClass} text-[var(--color-text-muted)] mb-2`}>
                {t('projects.activeCount')}
              </p>
              <p className={`${countClass} text-[var(--color-accent-primary)]`}>
                {activeProjectCount}
              </p>
            </div>
            {/* 保留中は数字自体をボタンにする（カード全体ではなく、この列だけがクリック可能）。 */}
            <div className="flex flex-col items-center justify-center">
              <p className={`${labelClass} text-[var(--color-text-muted)] mb-2`}>
                {t('projects.onHoldCount')}
              </p>
              <button
                type="button"
                onClick={onShowOnHold}
                aria-label={t('projects.onHoldCount')}
                title={t('projects.clickToView')}
                className={`${countClass} text-[var(--color-warning)] cursor-pointer rounded transition-opacity hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-border-focus)]`}
              >
                {onHoldCount}
              </button>
            </div>
            {crossDivPmoUi && (
              <div className="flex flex-col items-center justify-center">
                <p className={`${labelClass} text-[var(--color-text-muted)] mb-2`}>
                  {t('projects.monthlyActualPoint')}
                </p>
                <p
                  className="font-mono text-3xl font-bold tabular-nums"
                  style={{ color: crossDivPmoUi.divBadgeColor }}
                >
                  {formatPt(monthlyActualPoint)}
                  <span className="ml-1 text-xl">pt</span>
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      <ReviewerResourceCard />
      <HealthPieCard healthStats={healthStats} healthGrade={healthGrade} />
    </div>
  );
}
